import { ArrowLeft, Zap, Target, TrendingUp, Award, Users, Lock, Handshake, Mail, Phone } from 'lucide-react';
import { useEffect } from 'react';
import ketteraLogo from './images/kettera_logo.png';
import { T } from './LanguageContext';

function GrupoKettera({ onBack }) {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-blue-50/30 to-white overflow-x-hidden">
      {/* Header fijo elegante */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl shadow-sm border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="h-24 sm:h-28 md:h-32 flex items-center justify-between gap-6">
            <button 
              onClick={onBack}
              className="flex items-center gap-3 px-5 py-3 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-2xl font-medium transition-all duration-300 group border border-transparent hover:border-blue-200"
            >
              <ArrowLeft className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform duration-300" />
              <span className="hidden sm:inline text-lg">Volver</span>
            </button>
            <a href="#" onClick={e => { e.preventDefault(); onBack(); }} className="absolute left-1/2 transform -translate-x-1/2">
              <img src={ketteraLogo} alt="Kettera" className="h-20 sm:h-24 md:h-28 object-contain w-auto hover:scale-105 transition-transform duration-300" />
            </a>
            <div className="w-32"></div>
          </div>
        </div>
      </header>

      <div className="h-24 sm:h-28 md:h-32"></div>

      {/* Hero Section */}
      <section className="relative pt-16 pb-24 bg-gradient-to-b from-white to-blue-50/50 overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-200/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-blue-200/10 to-transparent rounded-full blur-3xl"></div>
        
        <div className="max-w-[900px] mx-auto px-6 md:px-12 relative z-10">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 text-center mb-6 tracking-tight animate-fadeIn">
            GRUPO KETTERA
          </h1>

          <div className="w-32 h-1.5 bg-gradient-to-r from-[#2B6CB0] via-[#3182CE] to-[#2B6CB0] mx-auto rounded-full mb-12 animate-fadeIn shadow-lg"></div>

          <div className="space-y-6 text-lg text-gray-700 text-justify leading-relaxed">
            <p className="text-gray-800 leading-8 hover:text-gray-900 transition-colors duration-300">
              Somos una Institución Financiera con sede en Estados Unidos. Nos dedicamos a impulsar el desarrollo de las pymes, entendiendo los desafíos a los que se enfrentan para acceder a capital, por medio de factoraje.
            </p>
            <p className="text-gray-800 leading-8 hover:text-gray-900 transition-colors duration-300">
              En Kettera, nos apasiona ofrecer una experiencia excepcional. Nos esforzamos por reemplazar la confusión y la incertidumbre por claridad y control. Con un equipo de profesionales altamente especializados y el uso de tecnología de punta, impactamos este sector reinventando el crédito garantizado ofreciendo el mejor producto de su clase.
            </p>
            <p className="text-gray-800 leading-8 hover:text-gray-900 transition-colors duration-300">
              Somos mucho más que una empresa de factoraje de cuentas por cobrar. Nuestra motivación es ser un socio confiable y accesible para las PYMES que buscan soluciones financieras ágiles y flexibles.
            </p>
            <p className="text-gray-800 leading-8 hover:text-gray-900 transition-colors duration-300">
              Nos respalda uno de los fondos más reconocidos en el mercado, lo que nos permite eliminar los límites tradicionales y ofrecer términos transparentes y rápidos en nuestros servicios. Ofrecemos soluciones que se adaptan a las necesidades específicas de nuestro clientes.
            </p>
            <p className="text-gray-800 leading-8 hover:text-gray-900 transition-colors duration-300">
              Estamos aquí para ser el socio en el éxito financiero de tu empresa, puedes confiar en que recibirás el apoyo y la atención que mereces para mantener un flujo de efectivo saludable y alcanzar tus metas empresariales.
            </p>
            <p className="text-gray-700 italic leading-8 hover:text-gray-900 transition-colors duration-300">
              Somos una Institución Financiera No Bancaria registrada bajo las Leyes Mexicanas (Condusef).
            </p>
          </div>
        </div>
      </section>

      {/* Sección: Expertos en Factoraje Financiero */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight">Somos expertos en Factoraje Financiero</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#2B6CB0] to-[#3182CE] mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 gap-6 stagger-container">
            {/* Tarjeta 1 */}
            <div className="bg-gradient-to-br from-white to-blue-50/30 border border-blue-100 rounded-3xl p-8 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:border-blue-300 group flex items-start gap-6 stagger-item">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Flujo de Efectivo</h3>
                <p className="text-gray-700 leading-relaxed">Por medio de un simple proceso en línea, obtén los recursos en máximo 48 horas. Es mucho más fácil de lo que crees.</p>
              </div>
            </div>

            {/* Tarjeta 2 */}
            <div className="bg-gradient-to-br from-white to-blue-50/30 border border-blue-100 rounded-3xl p-8 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:border-blue-300 group flex items-start gap-6 stagger-item">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                <Target className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Objetivos Alineados</h3>
                <p className="text-gray-700 leading-relaxed">El crecimiento de tu Empresa es el crecimiento de la nuestra. Estamos para ayudarte a llegar dónde has soñado todo este tiempo.</p>
              </div>
            </div>

            {/* Tarjeta 3 */}
            <div className="bg-gradient-to-br from-white to-blue-50/30 border border-blue-100 rounded-3xl p-8 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:border-blue-300 group flex items-start gap-6 stagger-item">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                <TrendingUp className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Tus ventas como tu activo principal</h3>
                <p className="text-gray-700 leading-relaxed">Tu empresa tiene activos que nadie más entiende. Nosotros te decimos cuáles son.</p>
              </div>
            </div>

            {/* Tarjeta 4 */}
            <div className="bg-gradient-to-br from-white to-blue-50/30 border border-blue-100 rounded-3xl p-8 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:border-blue-300 group flex items-start gap-6 stagger-item">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                <Award className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Tu éxito</h3>
                <p className="text-gray-700 leading-relaxed">Vamos de la mano contigo hasta el final. Estamos para ayudar a tu empresa a posicionarse como líder en su ramo.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sección: ¿Por qué Kettera? (Principal) */}
      <section className="py-20 bg-gradient-to-b from-blue-50/50 to-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-300/10 to-transparent rounded-full blur-3xl"></div>
        <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4 tracking-tight">Seriedad y profesionalismo</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-[#2B6CB0] to-[#3182CE] mx-auto rounded-full"></div>
        </div>
        <div className="max-w-[900px] mx-auto px-6 md:px-12 relative z-10">
          <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-3xl p-12 border border-blue-100 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-blue-500/5 to-blue-500/0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div className="relative z-10">
              <h2 className="text-4xl font-bold text-gray-900 text-center mb-8 tracking-tight">¿Por qué Kettera?</h2>
              <p className="text-gray-800 text-lg leading-8 text-center">
                En Kettera nos hemos establecido como la fuente de refenercia para los servicios de última tecnología en factoraje. Además nuestra plataforma es muy conocida para la comunidad financiera internacional.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Seriedad y Profesionalismo - Debajo de ¿Por qué Kettera? */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 gap-6 stagger-container">
            {/* Tarjeta Expertos */}
            <div className="bg-gradient-to-br from-white to-blue-50/30 border border-blue-100 rounded-3xl p-8 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:border-blue-300 group flex items-start gap-6 stagger-item">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                <Award className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Expertos</h3>
                <p className="text-gray-700 leading-relaxed">Encontrarás ayuda profesional de nuestros expertos y especialistas en Finanzas como nunca antes.</p>
              </div>
            </div>

            {/* Tarjeta Privacidad */}
            <div className="bg-gradient-to-br from-white to-blue-50/30 border border-blue-100 rounded-3xl p-8 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:border-blue-300 group flex items-start gap-6 stagger-item">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Privacidad</h3>
                <p className="text-gray-700 leading-relaxed">No te preocupes por tu información personal. Garantizamos 100% de privacidad, seguridad y encriptación.</p>
              </div>
            </div>

            {/* Tarjeta Trabajo en Equipo */}
            <div className="bg-gradient-to-br from-white to-blue-50/30 border border-blue-100 rounded-3xl p-8 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:border-blue-300 group flex items-start gap-6 stagger-item">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                <Handshake className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Trabajo en Equipo</h3>
                <p className="text-gray-700 leading-relaxed">Te prometemos el mejor trabajo en equipo posible: tu negocio, tus ideas combinadas con nuestro asesoramiento experto para lograr el éxito.</p>
              </div>
            </div>

            {/* Tarjeta Soporte */}
            <div className="bg-gradient-to-br from-white to-blue-50/30 border border-blue-100 rounded-3xl p-8 shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:border-blue-300 group flex items-start gap-6 stagger-item">
              <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Soporte</h3>
                <p className="text-gray-700 leading-relaxed">Obtendrás soporte útil de nuestro equipo de atención al cliente, siempre que lo necesites, durante toda la semana.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sección de contacto elegante en Footer */}
      <section className="py-20 bg-gradient-to-b from-white to-blue-50/50 relative overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 relative z-10">
          <div className="mt-20 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50 rounded-3xl p-12 border border-blue-100 shadow-2xl hover:shadow-3xl transition-all duration-500 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-blue-200/20 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-indigo-200/20 to-transparent rounded-full blur-3xl"></div>
            
            <div className="relative z-10">
              <h3 className="text-5xl font-bold text-gray-900 mb-10 text-center animate-fadeIn">
                <T es="Contacto" en="Contact" />
              </h3>

              <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                <a href="mailto:atencion@ketterafs.com" className="flex flex-col items-center gap-4 p-8 bg-white rounded-3xl hover:shadow-2xl transition-all duration-500 group/card border border-blue-100 hover:border-blue-300 hover:-translate-y-2">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white group-hover/card:scale-110 group-hover/card:rotate-6 transition-all duration-500 shadow-lg">
                    <Mail className="w-8 h-8" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 font-medium mb-2 uppercase tracking-wide">
                      <T es="Correo electrónico" en="Email" />
                    </p>
                    <p className="text-blue-600 font-semibold text-lg group-hover/card:text-blue-700 transition-colors">
                      atencion@ketterafs.com
                    </p>
                  </div>
                </a>

                <a href="tel:+525549528609" className="flex flex-col items-center gap-4 p-8 bg-white rounded-3xl hover:shadow-2xl transition-all duration-500 group/card border border-blue-100 hover:border-blue-300 hover:-translate-y-2">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white group-hover/card:scale-110 group-hover/card:rotate-6 transition-all duration-500 shadow-lg">
                    <Phone className="w-8 h-8" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-500 font-medium mb-2 uppercase tracking-wide">
                      <T es="Teléfono" en="Phone" />
                    </p>
                    <p className="text-blue-600 font-semibold text-lg group-hover/card:text-blue-700 transition-colors">
                      55 4952 8609
                    </p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer decorativo */}
      <div className="w-full flex justify-center py-20">
        <div className="h-1.5 w-1/2 bg-gradient-to-r from-[#2B6CB0]/0 via-[#2B6CB0]/40 to-[#3182CE]/0 rounded-full shadow-lg"></div>
      </div>
    </div>
  );
}

export default GrupoKettera;
